import React, { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  Button,
  Card,
  CardBody,
  Col,
  Row,
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Spinner,
  FormGroup,
  Label,
  Input,
  FormFeedback,
} from "reactstrap";
import FieldEditor from "../components/FieldEditor";
import FormPreview from "../components/FormPreview";
// import "../styles/edit-form.css";
import MandatoryNotesCard from "../components/MandatoryNotesCard";
import {
  PALETTE,
  MAX_FIELDS,
  MANDATORY_FIELD_IDS,
  normalizeFieldsForApi,
  generateFieldId,
  generateFieldLabel,
} from "../utils/formBuilderUtils";
import { useFieldValidation } from "../utils/useFieldValidation";
import api from "../api";
import { useAuth } from "../auth/AuthContext";
import Sidebar from "../components/Sidebar";
import { showError, showReminder, showSuccess } from "../components/AppToast";
import {
  Info,
  Eye,
  EyeOff,
  Settings,
  GripVertical,
  X,
  ListChecks,
  Receipt,
  Plus,
  Trash2
} from "lucide-react";

// Only Amount is a true mandatory/locked field now — always present,
// always required, never removable, always receipt-enabled. Email and
// Phone are NOT mandatory anymore: they're ordinary optional fields, same
// as any other palette field type — draggable in, freely required/optional,
// and removable like everything else.
//
// Note: MANDATORY_FIELD_IDS (imported above) is still used by
// generateFieldId/generateFieldLabel purely to avoid id/label collisions
// with reserved ids (mandatory-amount, mandatory-email, mandatory-phone)
// when a user drops a new field from the palette — that's a separate
// concern from which fields are actually *required* to save the form.
const REQUIRED_FIELD_IDS = ["mandatory-amount"];

// Maximum number of fields that can have "receipt" enabled at once, whether
// toggled individually in FieldEditor or via the "Enable receipt for all fields" global
// toggle. Keep in sync with FormSetup.jsx.
const RECEIPT_FIELD_LIMIT = 30;

export default function EditForm() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { token } = useAuth();

  const [fields, setFields] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [show, setShow] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const debounceRef = useRef(null);

  const [selectedFieldId, setSelectedFieldId] = useState(null);
  const [showLivePreview, setShowLivePreview] = useState(false);
  const [showReceiptOrderValidation, setShowReceiptOrderValidation] =
    useState(false);
  const {
    hasFieldValidationErrors,
    handleFieldValidationChange,
    clearFieldError,
  } = useFieldValidation();

  const [title, setTitle] = useState("");
  const [mode, setMode] = useState("");
  const [slugg, setSlug] = useState("");
  const [description, setDescription] = useState("");
  const [merchantId, setMerchantId] = useState("");
  const [merchEmail, setMerchEmail] = useState("");
  const [merchPhone, setMerchPhone] = useState("");
  const [merchUsername, setMerchUsername] = useState("");
  const [merchPassword, setMerchPassword] = useState("");
  const [logo, setLogo] = useState(null);
  const [secondaryLogo, setSecondaryLogo] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [previewStatus, setPreviewStatus] = useState("");
  const [environment, setEnvironment] = useState("U");
  const [merchantIds, setMerchantIds] = useState([]);
  const [emailReceipt, SetEmailReceipt] = useState();

  const [isConfigModalOpen, setIsConfigModalOpen] = useState(false);
  const [tempMerchantId, setTempMerchantId] = useState("");
  const [tempEnvironment, setTempEnvironment] = useState("U");
  const [tempEmail, setTempEmail] = useState("");
  const [tempPhone, setTempPhone] = useState("");
  const [tempTitle, setTempTitle] = useState("");
  const [tempUsername, setTempUsername] = useState("");
  const [tempPassword, setTempPassword] = useState("");
  const [tempLogo, setTempLogo] = useState(null);
  const [tempSecondaryLogo, setTempSecondaryLogo] = useState(null);
  const [tempDescription, setTempDescription] = useState("");
  const [tempEmailReceipt, setTempEmailReceipt] = useState("");
  const [emailError, setEmailError] = useState("");
  const [phoneError, setPhoneError] = useState("");
  const [showUdfModal, setShowUdfModal] = useState(false);
  const [passwordError, setPasswordError] = useState("");
  const [stakeholderNotification, setStakeholderNotification] =
    useState(false);

  const [stakeholderEmails, setStakeholderEmails] =
    useState([]);

  const [tempStakeholderNotification,
    setTempStakeholderNotification] =
    useState(false);

  const [tempStakeholderEmails,
    setTempStakeholderEmails] =
    useState([""]);
  const [merchantNotification, setMerchantNotification] =
    useState(false);

  const [tempMerchantNotification,
    setTempMerchantNotification] =
    useState(false);
  // const {role}=useAuth();
  // if(!role){
  //   return null;
  // }
  const encoded = localStorage.getItem("role");
  if (!encoded) { window.location.href = "/gfb-admin/login"; }
  const role = atob(encoded);

  const [udfMappings, setUdfMappings] = useState({});
  const BASEURL = process.env.REACT_APP_BASE_URL_DEV;

  const handleSecondaryLogoChange =
    (e) => {

      const file =
        e.target.files[0];

      if (!file) return;

      if (
        file.size >
        2 * 1024 * 1024
      ) {
        showError(
          "Image size should not exceed 2 MB."
        );
        return;
      }

      setTempSecondaryLogo(
        file
      );
    };
  useEffect(() => { if (token) api.setToken(token); }, [token]);

  useEffect(() => {
    const fetchFormData = async () => {
      setLoading(true);
      setErr("");
      if (location.state?.isActive !== undefined) setIsActive(location.state.isActive);
      try {
        const activeParam = location.state?.isActive !== undefined ? `?isActive=${location.state.isActive}` : "";
        const response = await api.post(`/forms/slug/${slug}${activeParam}`, {}, {
          headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
        });
        const formData = response.data;
  
        setMode(formData.mode || "");
        setTitle(formData.title || "");
        setSlug(formData.slug || "");
        setDescription(formData.description || "");
        setMerchantId(formData.merchantId ? String(formData.merchantId) : "");
        setEnvironment(formData.env || "U");
        setMerchEmail(formData.merchEmail || "");
        setMerchPhone(formData.merchPhone || "");
        setMerchUsername(formData.adminUserName || "");
        SetEmailReceipt(formData.customReceipt);
        if (formData.logo) setLogo(formData.logo);
        if (formData.secondaryLogo) setSecondaryLogo(formData.secondaryLogo);
        if (location.state?.isActive === undefined && formData.isActive !== undefined) setIsActive(formData.isActive);
        if (formData.form_schema && Array.isArray(formData.form_schema.fields)) {
          setFields(formData.form_schema.fields.map((field) => {
            // Only a field with the reserved Amount id is treated as
            // mandatory now. A field previously saved with the old
            // mandatory-email / mandatory-phone id is loaded as a normal
            // optional field — its saved required/receipt state is kept
            // as-is, but it's no longer force-locked.
            const isMandatory = REQUIRED_FIELD_IDS.includes(field.id);
            if (!isMandatory) {
              return { ...field, mandatory: false, receiptEnabled: field.receiptEnabled ?? false, receiptEmailEnabled: field.receiptEmailEnabled ?? false ,cascadeEnabled: field.cascadeEnabled ?? false,parentFieldId: field.parentFieldId ?? null,cascadeLevel: field.cascadeLevel ?? 1,cascadeMappings: field.cascadeMappings ?? {},};
            }
            const isAmount = field.type === "amount";
            return {
              ...field,
              mandatory: true,
              required: true,
              // Amount is always included in the receipt, regardless of
              // what was previously saved for this form.
              receiptEnabled: isAmount ? true : (field.receiptEnabled ?? false),
              receiptEmailEnabled: field.receiptEmailEnabled ?? false,
              cascadeEnabled: field.cascadeEnabled ?? false,
              parentFieldId: field.parentFieldId ?? null,
              cascadeLevel: field.cascadeLevel ?? 1,
              cascadeMappings: field.cascadeMappings ?? {},

            };
          }));
          setStakeholderNotification(
            formData.form_schema
              ?.stakeholderNotification || false
          );

          setStakeholderEmails(
            formData.form_schema?.stakeholderEmails?.length
              ? formData.form_schema.stakeholderEmails
              : [""]
          );


          setMerchantNotification(
            formData.form_schema?.merchantNotification || false
          );
        } else {
          setFields([]);
        }
        try {
          const udfRes = await api.post(`/forms/slug/${slug}/udfget`, {}, {
            headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
          });
          setUdfMappings(udfRes.data || {});
        } catch (err) {
          console.error("Failed to load UDF mappings:", err);
          showError("Error loading UDF mappings");
        }
      } catch (error) {
        const errorMsg = error.response?.data?.error || error.message || "Failed to load form";
        setErr(errorMsg);
      } finally {
        setLoading(false);
      }
    };
    fetchFormData();
  }, [slug, token, location.state]);

  useEffect(() => {
    async function fetchMerchantIds() {
      try {
        const token = localStorage.getItem("jwt");
        const res = await api.post("/merchants/getMerchantId", {}, {
          headers: { Accept: "application/json", Authorization: `Bearer ${token}` },
        });
        const data = res.data;
        setMerchantIds(Array.isArray(data) ? data.map((item) => (typeof item === "string" ? item : item.id)) : []);
      } catch (err) {
        console.error("Failed to load merchant IDs:", err);
      }
    }
    fetchMerchantIds();
  }, []);

  useEffect(() => {
    if (!query) { setResults([]); setShow(false); return; }
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await api.post(`/merchants/fechMerchantIds`, {
          params: { number: query },
          headers: { Accept: "application/json", "Content-Type": "application/json" },
        });
        if (response.status !== 200) throw new Error(`Search failed: ${response.status}`);
        const data = response.data;
        setResults(Array.isArray(data) ? data : []);
        setShow(true);
      } catch (err) {
        console.error("Merchant search error:", err);
        setResults([]);
        setShow(false);
      }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [query, BASEURL]);

  const toggleConfigModal = () => {
    if (!isConfigModalOpen) {
      setTempMerchantId(merchantId);
      setTempEnvironment(environment);
      setTempEmail(merchEmail);
      setTempPhone(merchPhone);
      setTempTitle(title);
      setTempUsername(merchUsername);
      setTempPassword("");
      setTempLogo(logo);
      setTempSecondaryLogo(secondaryLogo);
      setTempDescription(description);
      setTempEmailReceipt(emailReceipt);


      setTempStakeholderEmails(
        stakeholderEmails?.length
          ? stakeholderEmails
          : [""]
      );

      setTempMerchantNotification(
        merchantNotification
      );
    }
    setIsConfigModalOpen(!isConfigModalOpen);
  };

  const handleSaveConfiguration = () => {
    let valid = true;
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!tempEmail || !emailRegex.test(tempEmail)) { showError("Please enter a valid email address"); valid = false; }
    const phoneRegex = /^[0-9]{10}$/;
    if (!tempPhone || !phoneRegex.test(tempPhone)) { showError("Phone number must be exactly 10 digits"); valid = false; }
    if (!valid) return;
    setMerchantId(tempMerchantId);
    setEnvironment(tempEnvironment);
    setMerchEmail(tempEmail);
    setMerchPhone(tempPhone);
    setTitle(tempTitle);
    setMerchUsername(tempUsername);
    SetEmailReceipt(tempEmailReceipt);
    if (tempPassword && tempPassword.trim()) setMerchPassword(tempPassword);
    setLogo(tempLogo);
    setSecondaryLogo(tempSecondaryLogo);
    setDescription(tempDescription);
    showReminder();
    setIsConfigModalOpen(false);


    setStakeholderEmails(
      tempStakeholderEmails
    );
    setMerchantNotification(
      tempMerchantNotification
    );
  };

  const handleLogoChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 2 * 1024 * 1024) { showError("Image size should not exceed 2 MB."); return; }
      setTempLogo(file);
    }
  };

  // ── Drag & drop ──────────────────────────────────────────────────────────
  const handleDragEnd = (result) => {
    if (!result.destination) return;
    const { source, destination } = result;
    if (source.droppableId === "palette" && destination.droppableId === "canvas") {
      if (fields.length >= MAX_FIELDS) { showError(`Maximum ${MAX_FIELDS} fields allowed`); return; }
      const src = PALETTE[source.index];
      // generateFieldId/generateFieldLabel exclude MANDATORY_FIELD_IDS by
      // default, so an extra Email/Phone/Amount field dropped from the
      // palette never collides with the locked mandatory-* id and gets a
      // clean sequential id/label (email, email2, email3...).
      const newId = generateFieldId(src.type, fields);
      const newLabel = generateFieldLabel(src.label, fields);
      const instance = {
        id: newId, type: src.type, label: newLabel,
        options: src.options || [], required: false, mandatory: false,
        readOnly: false, splitRequired: false, fixedValueEnabled: false, fixedValue: "",
        receiptEnabled: false, receiptEmailEnabled: false, receiptOrder: null,
        cascadeEnabled: false,
parentFieldId: null,
cascadeLevel: 1,
cascadeMappings: {},
      };
      const arr = Array.from(fields);
      arr.splice(destination.index, 0, instance);
      setFields(arr);
      // Auto-select newly added field
      setSelectedFieldId(instance.id);
      return;
    }
    if (source.droppableId === "canvas" && destination.droppableId === "canvas") {
      const arr = Array.from(fields);
      const [moved] = arr.splice(source.index, 1);
      arr.splice(destination.index, 0, moved);
      setFields(arr);
    }
  };

  // ── Duplicate label check ─────────────────────────────────────────────────
  const hasDuplicateLabel = (label, excludeId) => {
    const normalized = label.trim().toLowerCase();
    return fields.some((f) => f.id !== excludeId && f.label.trim().toLowerCase() === normalized);
  };

 const updateField = (fieldId, newField) => {
  if (hasDuplicateLabel(newField.label, fieldId)) {
    showError(
      `Duplicate label "${newField.label}" — each field must have a unique label.`
    );
    return;
  }

  setErr("");

  setFields((prev) => {
    const updatedFields = prev.map((f) => {
      if (f.id !== fieldId) {
        return f;
      }

      const merged = {
        ...f,
        ...newField,
      };

      if (f.mandatory) {
        merged.required = true;
        merged.mandatory = true;
      }

      if (
        f.type === "amount" &&
        f.mandatory
      ) {
        merged.receiptEnabled = true;
      }

      return merged;
    });

    const updatedField =
      updatedFields.find(
        (f) => f.id === fieldId
      );

    if (
      !updatedField ||
      !Array.isArray(updatedField.options)
    ) {
      return updatedFields;
    }

    return updatedFields.map((f) => {
      let updatedMappings =
        f.cascadeMappings;

      // Parent option removed cleanup
      if (
        f.parentFieldId === fieldId &&
        f.cascadeMappings
      ) {
        updatedMappings = Object.fromEntries(
          Object.entries(
            f.cascadeMappings
          ).filter(([parentOption]) =>
            updatedField.options.includes(
              parentOption
            )
          )
        );
      }

      // Child option removed cleanup
      if (
        f.id === fieldId &&
        f.cascadeMappings
      ) {
        updatedMappings =
          Object.fromEntries(
            Object.entries(
              updatedMappings || {}
            ).map(
              ([parentOption, values]) => [
                parentOption,
                (values || []).filter(
                  (childValue) =>
                    updatedField.options?.includes(
                      childValue
                    )
                ),
              ]
            )
          );
      }

      return {
        ...f,
        cascadeMappings:
          updatedMappings,
      };
    });
  });
};

  const removeField = (fieldId, fieldLabel) => {
    const udfName = Object.keys(udfMappings).find((key) => udfMappings[key] === fieldId);
    if (udfName) showError(`Field "${fieldLabel}" is mapped to ${udfName}. Removing it will also clear that UDF mapping.`);
   setFields((prev) =>
  prev
    .filter(
      (f) =>
        f.id !== fieldId ||
        f.mandatory
    )
    .map((f) => {
      if (
        f.parentFieldId === fieldId
      ) {
        return {
          ...f,
          cascadeEnabled: false,
          parentFieldId: null,
          cascadeLevel: 1,
          cascadeMappings: {},
        };
      }

      return f;
    })
);
    if (udfName) setUdfMappings((prev) => ({ ...prev, [udfName]: "" }));
    clearFieldError(fieldId);
    if (selectedFieldId === fieldId) setSelectedFieldId(null);
  };

  // ── Global bulk toggles ─────────────────────────────────────────────────
  const allRequired = fields.length > 0 && fields.every((f) => !!f.required);
  const allReceiptEnabled = fields.length > 0 && fields.every((f) => !!f.receiptEnabled);

  // "Required for All" toggle — mandatory fields (Amount) stay required
  // regardless.
  const toggleRequiredForAll = () => {
    if (!fields.length) return;
    if (allRequired) {
      setFields((prev) => prev.map((f) => ({ ...f, required: f.mandatory ? true : false })));
    } else {
      setFields((prev) => prev.map((f) => ({ ...f, required: true })));
    }
  };

  // "Enable receipt for all fields" toggle — blocked with an error toast when switching ON
  // past RECEIPT_FIELD_LIMIT. Switching OFF always works — except Amount,
  // which always stays in the receipt no matter what.
  const toggleReceiptForAll = () => {
    if (!fields.length) return;
    if (allReceiptEnabled) {
      setFields((prev) => prev.map((f) => ({
        ...f,
        receiptEnabled: (f.type === "amount" && f.mandatory) ? true : false,
      })));
      return;
    }
    if (fields.length > RECEIPT_FIELD_LIMIT) {
      showError(`Receipt can be enabled for a maximum of ${RECEIPT_FIELD_LIMIT} fields. This form currently has ${fields.length} fields.`);
      return;
    }
    if (hasReceiptOrderError) {
      setShowReceiptOrderValidation(true);

      showError(
        "Please select receipt display order for all receipt-enabled fields."
      );

      return;
    }
    setShowReceiptOrderValidation(false);
    setFields((prev) => prev.map((f) => ({ ...f, receiptEnabled: true })));
  };

  const handleUpdateUdfMappings = async () => {
    try {
      await api.post(`/forms/slug/${slug}/udf`, udfMappings, {
        headers: { Authorization: `Bearer ${token}`, "Content-Type": "application/json" },
      });
      showSuccess("UDF mappings saved successfully!");
    } catch (err) {
      console.error("Failed to save UDF mappings:", err);
      showError("Error saving UDF mappings");
    }
  };

  const canSave = useMemo(() => {
    const hasMandatoryFields = REQUIRED_FIELD_IDS.every((mandFieldId) => fields.some((f) => f.id === mandFieldId));
    return title?.trim() && slugg?.trim() && fields.length > 0 && merchantId && hasMandatoryFields && !hasFieldValidationErrors;
  }, [title, slugg, fields, merchantId, hasFieldValidationErrors]);
// If you want every parent option to have at least one mapping:
//   const validateCascadeMappings = () => {
//   for (const field of fields) {
//     if (
//       field.cascadeEnabled &&
//       field.parentFieldId
//     ) {
//       const parentField = fields.find(
//         (f) => f.id === field.parentFieldId
//       );

//       for (const parentOption of parentField?.options || []) {
//         const mapping =
//           field.cascadeMappings?.[parentOption] || [];

//         if (mapping.length === 0) {
//           showError(
//             `Please configure mapping for "${parentOption}" in "${field.label}".`
//           );
//           return false;
//         }
//       }
//     }
//   }

//   return true;
// };
const validateCascadeMappings = () => {
  for (const field of fields) {
    if (
      field.cascadeEnabled &&
      field.parentFieldId
    ) {
      const mappings =
        field.cascadeMappings || {};
        const parentField = fields.find(
  (f) => f.id === field.parentFieldId
);

// if (parentField?.options?.length) {
//   for (const parentOption of parentField.options) {
//     const mappedValues =
//       mappings[parentOption] || [];

//     if (mappedValues.length === 0) {
//       showError(
//         `Please configure mapping for parent option "${parentOption}" in "${field.label}".`
//       );
//       return false;
//     }
//   }
// }

      // At least one mapping must exist
      const hasMapping = Object.values(
        mappings
      ).some(
        (values) =>
          Array.isArray(values) &&
          values.length > 0
      );

      if (!hasMapping) {
        showError(
          `Please configure value mapping for "${field.label}".`
        );
        return false;
      }

      // No duplicate mapping sets
      // const mappingSets = Object.values(
      //   mappings
      // )
      //   .filter(
      //     (values) =>
      //       Array.isArray(values) &&
      //       values.length > 0
      //   )
      //   .map(
      //     (values) =>
      //       [...values]
      //         .sort()
      //         .join("|")
      //   );

      // if (
      //   new Set(mappingSets).size !==
      //   mappingSets.length
      // ) {
      //   showError(
      //     `Duplicate value mappings found in "${field.label}". Different parent options cannot have the exact same child mappings.`
      //   );
      //   return false;
      // }

      // Required child cannot have any optional ancestor
      if (field.required || field.mandatory) {
        let currentParentId =
          field.parentFieldId;

        while (currentParentId) {
          const parentField =
            fields.find(
              (f) =>
                f.id ===
                currentParentId
            );

          if (!parentField) {
            break;
          }

          if (
            !parentField.required &&
            !parentField.mandatory
          ) {
            showError(
              `"${field.label}" is required, so parent field "${parentField.label}" and all ancestor cascade fields must also be required.`
            );
            return false;
          }

          currentParentId =
            parentField.parentFieldId;
        }
      }
    }
  }

  return true;
};
  const handleSaveClick = () => {
    setErr("");
    if (hasFieldValidationErrors) { setErr("Please fix all fixed value errors before saving."); return; }
    if (!validateCascadeMappings()) {
  return;
}
    if (!title?.trim()) { setErr("Title is required. Please set it in basic details."); return; }
    if (!slugg?.trim()) { setErr("Slug is required. Please set it in basic details."); return; }
    if (!merchantId) { setErr("Merchant ID is required. Please set it in basic details."); return; }
    if (fields.length === 0) { setErr("Please add at least one field to the form."); return; }
    const hasMandatoryFields = REQUIRED_FIELD_IDS.every((mandFieldId) => fields.some((f) => f.id === mandFieldId));
    if (!hasMandatoryFields) { setErr("Amount field is mandatory and must be present."); return; }
    const receiptCount = fields.filter(
      (f) => f.receiptEnabled
    ).length;

    if (receiptCount > RECEIPT_FIELD_LIMIT) {
      setErr(
        `Receipt can be enabled for a maximum of ${RECEIPT_FIELD_LIMIT} fields.`
      );
      return;
    }

    const receiptFields = fields.filter(
      (f) => f.receiptEnabled
    );

    const fieldsWithOrder = receiptFields.filter(
      (f) =>
        f.receiptOrder !== null &&
        f.receiptOrder !== undefined &&
        f.receiptOrder !== ""
    );

    const fieldsWithoutOrder = receiptFields.filter(
      (f) =>
        f.receiptOrder === null ||
        f.receiptOrder === undefined ||
        f.receiptOrder === ""
    );

    const hasReceiptOrderError =
      fieldsWithOrder.length > 0 &&
      fieldsWithoutOrder.length > 0;

    if (hasReceiptOrderError) {
      setShowReceiptOrderValidation(true);

      showError(
        "Please select receipt display order for all receipt-enabled fields."
      );

      return;
    }

    setShowReceiptOrderValidation(false);

    setShowConfirmModal(true);
    // const receiptCount = fields.filter((f) => f.receiptEnabled).length;
    // if (receiptCount > RECEIPT_FIELD_LIMIT) { setErr(`Receipt can be enabled for a maximum of ${RECEIPT_FIELD_LIMIT} fields.`); return; }
    // setShowConfirmModal(true);
  };

  const onSave = async () => {
    setShowConfirmModal(false);
    setSaving(true);
    try {
      const token = localStorage.getItem("jwt");
      const formData = new FormData();
      const formObject = {
        title, slug: slugg, description, merchantId, isActive, stakeholderNotification: true, stakeholderEmails: stakeholderEmails.filter(
          (e) => e && e.trim()
        ), merchantNotification,
        form_schema: {
          fields: normalizeFieldsForApi(fields), udfMappings, stakeholderNotification: true, stakeholderEmails: stakeholderEmails.filter(
            (e) => e && e.trim()
          ), merchantNotification,
        },
        env: environment, merchEmail, merchPhone,
        username: merchUsername, customReceipt: emailReceipt, udfMappings,
      };
      if (merchPassword && merchPassword.trim()) formObject.password = merchPassword;
      formData.append("form", new Blob([JSON.stringify(formObject)], { type: "application/json" }));
      if (logo && logo instanceof File) formData.append("logo", logo);
      if (
        secondaryLogo &&
        secondaryLogo instanceof File
      ) {
        formData.append(
          "secondaryLogo",
          secondaryLogo
        );
      }
      const avatarBase64 = localStorage.getItem("avatar");
      if (avatarBase64) {
        const byteString = atob(avatarBase64.split(",")[1]);
        const mimeString = avatarBase64.split(",")[0].split(":")[1].split(";")[0];
        const ab = new ArrayBuffer(byteString.length);
        const ia = new Uint8Array(ab);
        for (let i = 0; i < byteString.length; i++) ia[i] = byteString.charCodeAt(i);
        formData.append("avatar", new Blob([ab], { type: mimeString }), "avatar.png");
      }
      const bgLogo = localStorage.getItem("avatar");
      if (bgLogo) formData.append("bgLogo", bgLogo);
      const config = { headers: { Authorization: `Bearer ${token}` } };
      if (slug) {
        await api.put(`/forms/update-form/${slug}`, formData, config);
        showSuccess("Form updated successfully!");
      } else {
        await api.post(`/forms`, formData, config);
        showSuccess("Form created successfully!");
      }
      navigate("/my-forms");
    } catch (e) {
      const errorMsg = e.response?.data?.error || e.message || "Save failed";
      if (errorMsg === "slug already exists") showError("Slug already exists. Please choose a different slug.");
      else showError(errorMsg);
      setErr(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = () => {
    if (!slugg?.trim()) { setErr("Slug is required to preview."); return; }
    window.open(`/forms/${slug}`, "_blank", "noopener,noreferrer");
  };

  const selectedField = fields.find((f) => f.id === selectedFieldId) || null;
  const receiptCount = fields.filter((f) => f.receiptEnabled).length;
  const receiptFields = fields.filter(
    (f) => f.receiptEnabled
  );

  const fieldsWithOrder = receiptFields.filter(
    (f) =>
      f.receiptOrder !== null &&
      f.receiptOrder !== undefined &&
      f.receiptOrder !== ""
  );

  const fieldsWithoutOrder = receiptFields.filter(
    (f) =>
      f.receiptOrder === null ||
      f.receiptOrder === undefined ||
      f.receiptOrder === ""
  );

  const hasReceiptOrderError =
    fieldsWithOrder.length > 0 &&
    fieldsWithoutOrder.length > 0;
  return (
    <div className="px-shell">
      <Sidebar />
      <main className="px-main">
        <h2 className="px-title">Form Management</h2>
        <div className="px-sub">Edit and refine your forms with a streamlined, responsive interface</div>

        {mode === "excel" && <MandatoryNotesCard />}
        {err && <div className="alert alert-danger mb-3">{err}</div>}
        {hasFieldValidationErrors && (
          <div className="alert alert-warning mb-3">
            One or more fields have invalid fixed values. Please fix them before saving.
          </div>
        )}

        {/* Status + Config bar */}
        <Card className="mb-3 shadow-sm">
          <CardBody className="py-3">
            <div className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <FormGroup className="mb-0 d-flex align-items-center gap-2">

                <span className={`badge ${isActive ? "bg-success" : "bg-secondary"}`}>
                  {isActive ? "Active" : "Inactive"}
                </span>
                <Label className="mb-0 text-muted small">
                  {isActive ? "Form is currently active" : "Form is currently inactive"}
                </Label>
                <div className="form-switch mb-0">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    id="isActiveSwitch"
                    checked={isActive}
                    onChange={(e) => setIsActive(e.target.checked)}
                  />
                </div>
              </FormGroup>
              <div className="d-flex align-items-center gap-2 flex-wrap">
                <div className="fb-toolbar-env">
                  <label htmlFor="envSelectEdit" className="fb-toolbar-env-label">Environment</label>
                  <select
                    className="fb-env-select"
                    id="envSelectEdit"
                    value={environment}
                    onChange={(e) => setEnvironment(e.target.value)}
                  >
                    <option value="P">Production</option>
                    <option value="U">UAT</option>
                  </select>
                </div>

                <div className="fb-toolbar-divider" />

                <div className="fb-toggle-group">
                  <div
                    className={`fb-toggle-chip ${allRequired ? "fb-toggle-chip-active" : ""} ${!fields.length ? "fb-toggle-chip-disabled" : ""}`}
                    onClick={toggleRequiredForAll}
                    role="switch"
                    aria-checked={allRequired}
                    tabIndex={fields.length ? 0 : -1}
                    title="Toggle 'required' for every field on the canvas"
                    onKeyDown={(e) => (e.key === " " || e.key === "Enter") ? toggleRequiredForAll() : null}
                  >
                    <ListChecks size={13} className="fb-toggle-chip-icon" />
                    <span className="fb-toggle-chip-label">Required for All</span>
                    <div className={`fe-switch ${allRequired ? "fe-switch-on" : ""}`}>
                      <div className="fe-switch-thumb" />
                    </div>
                  </div>

                  <div
                    className={`fb-toggle-chip ${allReceiptEnabled ? "fb-toggle-chip-active" : ""} ${!fields.length ? "fb-toggle-chip-disabled" : ""}`}
                    onClick={toggleReceiptForAll}
                    role="switch"
                    aria-checked={allReceiptEnabled}
                    tabIndex={fields.length ? 0 : -1}
                    title={`Toggle receipt for every field (max ${RECEIPT_FIELD_LIMIT} fields). Amount is always included.`}
                    onKeyDown={(e) => (e.key === " " || e.key === "Enter") ? toggleReceiptForAll() : null}
                  >
                    <Receipt size={13} className="fb-toggle-chip-icon" />
                    <span className="fb-toggle-chip-label">Enable receipt for all fields</span>
                    {receiptCount > 0 && (
                      <span className="fb-toggle-chip-count">{receiptCount}/{RECEIPT_FIELD_LIMIT}</span>
                    )}
                    <div className={`fe-switch ${allReceiptEnabled ? "fe-switch-on" : ""}`}>
                      <div className="fe-switch-thumb" />
                    </div>
                  </div>
                </div>

                <div className="fb-toolbar-divider" />

                <Button color="outline-secondary" size="sm" onClick={() => setShowUdfModal(true)}>UDF Mappings</Button>
                <Button color="primary" size="sm" onClick={toggleConfigModal}>Edit Form Configuration</Button>
                <button
                  className={`fb-preview-toggle ${showLivePreview ? "fb-preview-toggle-active" : ""}`}
                  onClick={() => setShowLivePreview((v) => !v)}
                >
                  {showLivePreview
                    ? <><EyeOff size={14} className="me-1" />Back to Fields</>
                    : <><Eye size={14} className="me-1" />Show Preview</>}
                </button>
              </div>
            </div>
          </CardBody>
        </Card>

        <DragDropContext onDragEnd={handleDragEnd}>
          <Row className="g-3">

            {/* ── Palette ──────────────────────────────────────────────── */}
            <Col md="2">
              <Card className="h-100">
                <CardBody>
                  <h6 className="mb-3 text-muted fw-semibold" style={{ fontSize: "0.75rem", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    Field Types
                  </h6>
                  <Droppable droppableId="palette" isDropDisabled={true}>
                    {(provided) => (
                      <div ref={provided.innerRef} {...provided.droppableProps}>
                        {PALETTE.map((p, idx) => (
                          <Draggable key={`${p.type}-${idx}`} draggableId={`pal-${p.type}-${idx}`} index={idx}>
                            {(prov, snapshot) => (
                              <div
                                ref={prov.innerRef}
                                {...prov.draggableProps}
                                {...prov.dragHandleProps}
                                className={`fb-palette-item ${snapshot.isDragging ? "fb-palette-item-dragging" : ""}`}
                                tabIndex={0}
                                aria-label={`${p.label} draggable`}
                              >
                                <GripVertical size={12} className="text-muted me-1" />
                                {p.label}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </CardBody>
              </Card>
            </Col>

            {/* ── Right side: Canvas/Properties OR Preview ──────────────── */}
            <Col md="10">
              {showLivePreview ? (
                <Card>
                  <CardBody>
                    <FormPreview fields={fields} />
                  </CardBody>
                </Card>
              ) : (
                <Row className="g-3">
                  {/* ── Canvas ───────────────────────────────────────────── */}
                  <Col md={selectedField ? "6" : "12"}>
                    <Card className="h-100">
                      <CardBody>
                        <div className="d-flex justify-content-between align-items-center mb-3">
                          <h6 className="mb-0">
                            Canvas{" "}
                            <span className="text-muted fw-normal" style={{ fontSize: "0.8rem" }}>
                              ({fields.length}/{MAX_FIELDS})
                            </span>
                          </h6>
                        </div>
                        <Droppable droppableId="canvas">
                          {(provided) => (
                            <div ref={provided.innerRef} {...provided.droppableProps} className="min-vh-25" aria-label="Drop area for form fields">
                              {loading && <div className="text-muted small">Loading fields…</div>}
                              {!loading && fields.length === 0 && (
                                <div className="fb-canvas-empty">
                                  <p className="text-muted small mb-0">Drag fields here from the palette</p>
                                </div>
                              )}
                              {fields.map((field, index) => {
                                const isSelected = selectedFieldId === field.id;
                                return (
                                  <Draggable
                                    key={field.id}
                                    draggableId={field.id}
                                    index={index}
                                    isDragDisabled={!!field.isSearchKey}
                                  >
                                    {(prov, snapshot) => (
                                      <div
                                        ref={prov.innerRef}
                                        {...prov.draggableProps}
                                        {...prov.dragHandleProps}
                                        onClick={() => setSelectedFieldId(isSelected ? null : field.id)}
                                        className={[
                                          "fb-canvas-field",
                                          isSelected ? "fb-canvas-field-selected" : "",
                                          snapshot.isDragging ? "fb-canvas-field-dragging" : "",
                                          field.isSearchKey ? "fb-canvas-field-searchkey" : field.mandatory ? "fb-canvas-field-mandatory" : "",
                                        ].filter(Boolean).join(" ")}
                                        tabIndex={0}
                                        aria-label={`Field ${field.label} ${field.mandatory ? "mandatory" : "draggable"}`}
                                      >
                                        <div className="fb-canvas-field-inner">
                                          <div className="fb-canvas-field-drag">
                                            <GripVertical size={14} className="text-muted" />
                                          </div>
                                          <div className="fb-canvas-field-info">
                                            <span className="fb-canvas-field-label">{field.label || <em className="text-muted">Untitled</em>}</span>
                                            <span className="fb-canvas-field-type">{field.type}</span>
                                          </div>
                                          <div className="fb-canvas-field-badges">
                                            {field.cascadeEnabled && (
  <span
    className="badge bg-primary"
  >
    Cascade L{field.cascadeLevel}
  </span>
)}
                                            {field.receiptEnabled &&
                                              field.receiptOrder != null &&
                                              field.receiptOrder !== "" && (
                                                <span
                                                  className="badge"
                                                  style={{
                                                    fontSize: "0.65rem",
                                                    backgroundColor: "#4f46e5",
                                                    color: "#fff",
                                                  }}
                                                >
                                                  Order: {field.receiptOrder}
                                                </span>
                                              )}
                                            {field.isSearchKey && <span className="badge bg-success" style={{ fontSize: "0.65rem" }}>Search Key</span>}
                                            {field.mandatory && !field.isSearchKey && <span className="badge bg-danger" style={{ fontSize: "0.65rem" }}>Mandatory</span>}
                                            {field.required && !field.mandatory && !field.isSearchKey && (
                                              <span className="badge bg-primary" style={{ fontSize: "0.65rem" }}>Required</span>
                                            )}
                                            {field.receiptEnabled && (
                                              <span className="badge" style={{ fontSize: "0.65rem", backgroundColor: "#8b5cf6", color: "#fff" }}>
                                                Receipt
                                              </span>
                                            )}
                                            {isSelected && <Settings size={12} className="text-primary ms-1" />}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </CardBody>
                    </Card>
                  </Col>

                  {/* ── Properties Panel ─────────────────────────────────── */}
                  {selectedField && (
                    <Col md="6">
                      <Card className="h-100 fb-props-card">
                        <CardBody>
                          <div className="d-flex justify-content-between align-items-center mb-3">
                            <div>
                              <h6 className="mb-0">Properties</h6>
                              <small className="text-muted">{selectedField.label || "Untitled"} · {selectedField.type}</small>
                            </div>
                          </div>
                          <FieldEditor
                            field={selectedField}
                            fields={fields}
                            onUpdate={updateField}
                            onRemove={(id) => removeField(id, selectedField.label)}
                            hideMandatoryDelete={selectedField.mandatory}
                            mode={mode}
                            onValidationChange={handleFieldValidationChange}
                            receiptFieldLimit={RECEIPT_FIELD_LIMIT}
                            receiptEnabledCount={receiptCount}
                            // FieldEditor should render the receipt toggle as
                            // permanently ON/disabled when this is true, since
                            // Amount is always included in the receipt.
                            lockReceiptOn={selectedField.type === "amount" && selectedField.mandatory}
                          />
                        </CardBody>
                      </Card>
                    </Col>
                  )}
                </Row>
              )}

              {/* ── Actions ───────────────────────────────────────────────── */}
              <Card className="mt-3">
                <CardBody>
                  <div className="d-flex gap-2 flex-wrap">
                    {!saving && (
                      <Button type="button" color="primary" onClick={handleSaveClick} disabled={!canSave}>
                        {slug ? "Update" : "Save"}
                      </Button>
                    )}
                    <Button type="button" color="secondary" onClick={handlePreview} disabled={!isActive}>
                      Preview Public URL
                    </Button>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </DragDropContext>

        {/* ── Confirm modal ─────────────────────────────────────────────── */}
        <Modal isOpen={showConfirmModal} toggle={() => setShowConfirmModal(!showConfirmModal)} centered>
          <ModalHeader toggle={() => setShowConfirmModal(!showConfirmModal)}>
            Confirm {slug ? "Update" : "Save"}
          </ModalHeader>
          <ModalBody>
            <p className="mb-2">Are you sure you want to {slug ? "update" : "create"} this form?</p>
            <div className="small text-muted">
              <strong>Form Title:</strong> {title}<br />
              <strong>Slug:</strong> {slugg}<br />
              <strong>Fields:</strong> {fields.length} field(s)<br />
              <strong>Receipt-enabled Fields:</strong> {receiptCount}/{RECEIPT_FIELD_LIMIT}<br />
              <strong>Status:</strong> {isActive ? "Active" : "Inactive"}
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowConfirmModal(false)}>Cancel</Button>
            <Button color="primary" onClick={onSave}>Yes, {slug ? "Update" : "Save"}</Button>
          </ModalFooter>
        </Modal>

        {/* ── Config modal ──────────────────────────────────────────────── */}
        <Modal isOpen={isConfigModalOpen} toggle={toggleConfigModal} size="lg">
          <ModalHeader toggle={toggleConfigModal}>Edit Configuration</ModalHeader>
          <ModalBody>
            <Row>
              <Col md="6">
                <FormGroup>
                  <Label className="fw-semibold">Merchant ID *</Label>
                  <Input type="select" value={tempMerchantId} onChange={(e) => setTempMerchantId(e.target.value)} disabled={role === "MERCHANT"}>
                    <option value="">Select Merchant</option>
                    {merchantIds.map((id) => <option key={id} value={String(id)}>{id}</option>)}
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="fw-semibold">Environment</Label>
                  <Input type="select" value={tempEnvironment} onChange={(e) => setTempEnvironment(e.target.value)}>
                    <option value="P">Production</option>
                    <option value="U">UAT</option>
                  </Input>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="fw-semibold">Form Title</Label>
                  <Input type="text" placeholder="Enter form title" value={tempTitle} onChange={(e) => setTempTitle(e.target.value)} />
                </FormGroup>
              </Col>
              {/* <Col md="6">
                <FormGroup>
                  <Label className="fw-semibold">Merchant Email</Label>
                  <Input type="email" placeholder="Enter email" value={tempEmail} onChange={(e) => setTempEmail(e.target.value)} invalid={!!emailError} />
                  {emailError && <FormFeedback>{emailError}</FormFeedback>}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="fw-semibold">Merchant Phone</Label>
                  <Input type="tel" placeholder="Enter phone number" value={tempPhone} onChange={(e) => setTempPhone(e.target.value)} invalid={!!phoneError} />
                  {phoneError && <FormFeedback>{phoneError}</FormFeedback>}
                </FormGroup>
              </Col> */}
              <Col md="6">
                <FormGroup>
                  <Label className="fw-semibold">Admin Username</Label>
                  <Input type="text" placeholder="Enter username" value={tempUsername} onChange={(e) => setTempUsername(e.target.value)} />
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="fw-semibold">Change Admin Password</Label>
                  <Input
                    name="editAdminPassword" type="password" placeholder="Enter new password"
                    value={tempPassword} onChange={(e) => setTempPassword(e.target.value)}
                    onBlur={() => {
                      if (tempPassword && !/^(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[@$!%*?&])(?!.*(\d)\1{2,}).{8,}$/.test(tempPassword)) {
                        setPasswordError("Password must be at least 8 characters, include uppercase, lowercase, number, special character, and avoid continuous digits");
                      } else { setPasswordError(""); }
                    }}
                    invalid={!!passwordError}
                  />
                  {passwordError && <FormFeedback>{passwordError}</FormFeedback>}
                  <small className="text-muted">Leave blank to keep current password</small>
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="fw-semibold">
                    Primary Logo
                  </Label>

                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleLogoChange}
                  />

                  {tempLogo && (
                    <small className="text-muted d-block mt-1">
                      {tempLogo instanceof File
                        ? `Selected: ${tempLogo.name}`
                        : "Current logo uploaded"}
                    </small>
                  )}
                </FormGroup>
              </Col>

              <Col md="6">
                <FormGroup>
                  <Label className="fw-semibold">
                    Secondary Logo
                  </Label>

                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleSecondaryLogoChange}
                  />

                  {tempSecondaryLogo && (
                    <small className="text-muted d-block mt-1">
                      {tempSecondaryLogo instanceof File
                        ? `Selected: ${tempSecondaryLogo.name}`
                        : "Current secondary logo uploaded"}
                    </small>
                  )}
                </FormGroup>
              </Col>
                 <Col md="6">
                <FormGroup>
                  <Label className="fw-semibold">Merchant Phone</Label>
                  <Input type="tel" placeholder="Enter phone number" value={tempPhone} onChange={(e) => setTempPhone(e.target.value)} invalid={!!phoneError} />
                  {phoneError && <FormFeedback>{phoneError}</FormFeedback>}
                </FormGroup>
              </Col>
              <Col md="6">
                <FormGroup>
                  <Label className="fw-semibold">Merchant Email</Label>
                  <Input type="email" placeholder="Enter email" value={tempEmail} onChange={(e) => setTempEmail(e.target.value)} invalid={!!emailError} />
                  {emailError && <FormFeedback>{emailError}</FormFeedback>}
                </FormGroup>
              </Col>
                   <Col md="12">
                <FormGroup check>
                  <Label check>
                    <Input
                      type="checkbox"
                      checked={tempMerchantNotification}
                      onChange={(e) =>
                        setTempMerchantNotification(
                          e.target.checked
                        )
                      }
                    />
                    {" "}
                    Send Email Notification to Merchant
                  </Label>
                </FormGroup>
              </Col>

              <Col md="12">
                <FormGroup>
                  <Label className="fw-semibold">Description</Label>
                  <Input type="textarea" rows="3" placeholder="Enter form description" value={tempDescription} onChange={(e) => setTempDescription(e.target.value)} />
                </FormGroup>
              </Col>
              {/* <Col md="12">
                <FormGroup check>
                  <Label check>
                    <Input type="checkbox" checked={tempEmailReceipt} onChange={(e) => setTempEmailReceipt(e.target.checked)} />{" "}
                    Send receipt on email
                  </Label>
                </FormGroup>
              </Col> */}
         
              <Col md="12">
                <FormGroup check>
                  <Label check>
                    {/* <Input
                        type="checkbox"
                        checked={tempStakeholderNotification}
                        onChange={(e) =>
                          setTempStakeholderNotification(
                            e.target.checked
                          )
                        }
                      />
                      {" "}
                      Send Stakeholder Notification */}
                  </Label>
                </FormGroup>
              </Col>

              <Col md="12">
                <FormGroup>
                  <Label>
                    External Stakeholder Emails
                  </Label>

                  {tempStakeholderEmails.map((email, index) => (
                    <div
                      key={index}
                      className="mb-2"
                    >
                      <div className="d-flex gap-2 align-items-start">
                        <Input
                          type="email"
                          value={email}
                          placeholder="Enter stakeholder email"
                          invalid={
                            email &&
                            !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
                          }
                          onChange={(e) => {
                            const updated = [...tempStakeholderEmails];
                            updated[index] = e.target.value;
                            setTempStakeholderEmails(updated);
                          }}
                        />

                        {tempStakeholderEmails.length > 1 && (
                          <Button
                            type="button"
                            className="stakeholder-btn-delete"
                            size="sm"
                            onClick={() => {
                              setTempStakeholderEmails(
                                tempStakeholderEmails.filter(
                                  (_, i) => i !== index
                                )
                              );
                            }}
                          >
                            <Trash2 size={14} />
                          </Button>
                        )}

                        {index === tempStakeholderEmails.length - 1 &&
                          tempStakeholderEmails.length < 5 && (
                            <Button
                              type="button"
                              className="stakeholder-btn-add "
                              size="sm"
                              color ="primary"
                              onClick={() =>
                                setTempStakeholderEmails([
                                  ...tempStakeholderEmails,
                                  "",
                                ])
                              }
                            >
                              <Plus size={14} />
                            </Button>
                          )}
                      </div>

                      {email &&
                        !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) && (
                          <FormFeedback className="d-block">
                            Please enter a valid email address
                          </FormFeedback>
                        )}
                    </div>
                  ))}
                </FormGroup>
              </Col>

            </Row>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={toggleConfigModal}>Cancel</Button>
            <Button color="primary" onClick={handleSaveConfiguration}>Save and Close</Button>
          </ModalFooter>
        </Modal>

        {/* ── UDF modal ─────────────────────────────────────────────────── */}
        <Modal isOpen={showUdfModal} toggle={() => setShowUdfModal(false)} size="lg">
          <ModalHeader toggle={() => setShowUdfModal(false)}>Update UDF Mappings</ModalHeader>
          <ModalBody>
            <div className="d-flex justify-content-end mb-3">
              <Button color="danger" size="sm" onClick={() => setUdfMappings({})}>Clear All</Button>
            </div>
            <Row>
              {Array.from({ length: 10 }, (_, i) => {
                const udfName = `UDF${i + 1}`;
                const selectedField = udfMappings[udfName] || "";
                return (
                  <Col md={6} key={udfName}>
                    <FormGroup>
                      <Label>{udfName}</Label>
                      <Input
                        type="select"
                        value={selectedField}
                        onChange={(e) => {
                          const fieldId = e.target.value;
                          if (fieldId && fieldId !== "NONE") {
                            const alreadyMapped = Object.keys(udfMappings).find((key) => udfMappings[key] === fieldId && key !== udfName);
                            if (alreadyMapped) { showError(`Field "${fieldId}" is already mapped to ${alreadyMapped}. Duplicate selection is not allowed.`); return; }
                          }
                          setUdfMappings((prev) => ({ ...prev, [udfName]: fieldId }));
                        }}
                      >
                        <option value="" disabled>Select Field</option>
                        <option value="NONE">None</option>
                        {fields.map((f) => <option key={f.id} value={f.id}>{f.label} ({f.id})</option>)}
                      </Input>
                    </FormGroup>
                  </Col>
                );
              })}
            </Row>
            <div className="bulk-notes-card">
              <div className="bulk-notes-header"><Info size={16} /><span>Note</span></div>
              <ul className="udf-list">
                <li><strong>UDF1–UDF5</strong> support a maximum of 45 characters. For longer values, use <strong>UDF6–UDF10</strong>.</li>
              </ul>
            </div>
          </ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={() => setShowUdfModal(false)}>Cancel</Button>
            <Button color="primary" onClick={async () => { await handleUpdateUdfMappings(); setShowUdfModal(false); }}>Save</Button>
          </ModalFooter>
        </Modal>

        {loading && <div className="mt-3 text-muted small">Loading…</div>}
      </main>
    </div>
  );
}
